import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import {
  applyAffixes,
  assignStory,
  autoPair,
  batchIssue,
  defaultAdName,
  defaultAdSetIds,
  defaultTemplateAdId,
  initialProgress,
  itemsToRun,
  pairHasNoStory,
  planExpressBatch,
  progressCounts,
  type ExpressFile,
  type ExpressStatus,
  type PairingResult,
  type Progress,
} from '../../lib/metaExpressImages';
import {
  createDraft,
  getAssetUploadUrl,
  getDraft,
  getLiveAd,
  getLiveCampaignDetail,
  publishDraft,
  PublishFailedError,
  putFileWithProgress,
} from './api';
import { buildExpressDraftInput, droppedTextCount, templateIssues } from './expressFlow';
import { destinationLabel, firstLine } from './liveAdSetFlow';
import { validateFile } from './useAssetUpload';

// Express "Add images to a campaign": the same copy, many new images. The AM picks the ad
// sets and a template ad, drops images, fixes the Feed / Story pairs, and every chosen ad set
// gets one new ad per pair. Nothing existing is touched; each ad goes through the ordinary
// "add an ad to a live ad set" draft and publish, so ownership and Page checks all apply.

interface UploadedFile extends ExpressFile {
  file: File;
  previewUrl: string;
  state: 'uploading' | 'ready' | 'failed';
  url?: string;
  path?: string;
  error?: string;
}

async function readSize(file: File): Promise<{ width: number | null; height: number | null }> {
  try {
    const bmp = await createImageBitmap(file);
    const out = { width: bmp.width, height: bmp.height };
    bmp.close();
    return out;
  } catch {
    return { width: null, height: null };
  }
}

const errorText = (e: unknown): string =>
  e instanceof PublishFailedError
    ? (e.publishError?.message ?? e.message)
    : e instanceof ApiError
      ? e.message
      : e instanceof Error
        ? e.message
        : 'Failed';

export function ExpressImagesPage() {
  const [params] = useSearchParams();
  const accountId = params.get('account') ?? '';
  const campaignId = params.get('campaign') ?? '';

  const detail = useQuery({
    queryKey: ['metaLiveCampaign', accountId, campaignId],
    queryFn: () => getLiveCampaignDetail(accountId, campaignId),
    enabled: Boolean(accountId && campaignId),
  });

  const [adSetIds, setAdSetIds] = useState<string[] | null>(null);
  const selectedIds = adSetIds ?? (detail.data ? defaultAdSetIds(detail.data.adSets) : []);
  const chosenSets = (detail.data?.adSets ?? []).filter((s) => selectedIds.includes(s.id));

  const [templateId, setTemplateId] = useState<string | null>(null);
  const templateAds = chosenSets.flatMap((s) => s.ads);
  const effectiveTemplateId = templateId ?? defaultTemplateAdId(chosenSets[0]?.ads ?? []);
  const templateTreeAd = templateAds.find((a) => a.id === effectiveTemplateId) ?? null;
  const templateQ = useQuery({
    queryKey: ['metaLiveAd', accountId, campaignId, effectiveTemplateId],
    queryFn: () => getLiveAd(accountId, campaignId, effectiveTemplateId!),
    enabled: Boolean(accountId && campaignId && effectiveTemplateId),
  });
  const template = templateQ.data ?? null;

  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [pairing, setPairing] = useState<PairingResult>({ pairs: [], unassignedStoryIds: [] });
  const [names, setNames] = useState<Record<string, string>>({});
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');
  const [status, setStatus] = useState<ExpressStatus>('PAUSED');
  const [progress, setProgress] = useState<Progress>({});
  const [running, setRunning] = useState(false);
  const idCounter = useRef(0);

  // Re-pair whenever the file set changes; hand edits are kept in `pairing` until then.
  useEffect(() => {
    setPairing(autoPair(files.map(({ id, name, width, height }) => ({ id, name, width, height }))));
  }, [files.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fileById = useMemo(() => new Map(files.map((f) => [f.id, f])), [files]);

  async function addFiles(list: FileList | null) {
    if (!list) return;
    const incoming: UploadedFile[] = [];
    for (const file of Array.from(list)) {
      const bad = validateFile(file, 'image');
      const size = await readSize(file);
      idCounter.current += 1;
      incoming.push({
        id: `f${idCounter.current}`,
        name: file.name,
        file,
        previewUrl: URL.createObjectURL(file),
        ...size,
        state: bad ? 'failed' : 'uploading',
        error: bad ?? undefined,
      });
    }
    setFiles((cur) => [...cur, ...incoming]);
    for (const f of incoming.filter((x) => x.state === 'uploading')) {
      try {
        const u = await getAssetUploadUrl({ accountId, contentType: f.file.type, originalFilename: f.file.name });
        await putFileWithProgress(u.uploadUrl, f.file, u.requiredHeaders);
        setFiles((cur) => cur.map((x) => (x.id === f.id ? { ...x, state: 'ready', url: u.imageUrl, path: u.imagePath } : x)));
      } catch (e) {
        setFiles((cur) => cur.map((x) => (x.id === f.id ? { ...x, state: 'failed', error: errorText(e) } : x)));
      }
    }
  }

  function removeFile(id: string) {
    setFiles((cur) => cur.filter((f) => f.id !== id));
  }

  // Default names follow the template's naming plus the file stem; the bulk affixes apply on top.
  const nameFor = (pairId: string, stem: string) =>
    applyAffixes(names[pairId] ?? defaultAdName(templateTreeAd?.name, stem), { prefix, suffix });
  const finalNames = Object.fromEntries(pairing.pairs.map((p) => [p.id, nameFor(p.id, p.stem)]));

  const items = planExpressBatch({ adSetIds: chosenSets.map((s) => s.id), pairs: pairing.pairs, names: finalNames });
  const tIssues = templateIssues(template, chosenSets[0]?.destination ?? 'unknown');
  const anyUnknownDest = chosenSets.some((s) => s.destination === 'unknown');
  const mixedDest = new Set(chosenSets.map((s) => s.destination)).size > 1;
  const uploading = files.some((f) => f.state === 'uploading');
  const feedFailed = pairing.pairs.some((p) => fileById.get(p.feedId)?.state === 'failed');
  const issue: string | null =
    batchIssue(items) ||
    tIssues[0]?.message ||
    (anyUnknownDest ? 'One of the chosen ad sets has an unknown destination. Leave it out.' : '') ||
    (mixedDest ? 'Website and instant form ad sets cannot share one template. Run them as two batches.' : '') ||
    (uploading ? 'Images are still uploading.' : '') ||
    (feedFailed ? 'An image failed to upload. Remove it or add it again.' : '') ||
    null;
  const counts = progressCounts(progress);
  const started = Object.keys(progress).length > 0;

  async function run(runItems: typeof items) {
    if (!detail.data || !template) return;
    setRunning(true);
    try {
      for (const item of runItems) {
        const set = chosenSets.find((s) => s.id === item.adSetId)!;
        const pair = pairing.pairs.find((p) => p.id === item.pairId)!;
        const feed = fileById.get(pair.feedId)!;
        const story = pair.storyId ? (fileById.get(pair.storyId) ?? null) : null;
        if (!feed.url || !feed.path || (story && (!story.url || !story.path))) {
          setProgress((p) => ({ ...p, [item.key]: { status: 'failed', error: 'An image is not uploaded.' } }));
          continue;
        }
        setProgress((p) => ({ ...p, [item.key]: { ...p[item.key], status: 'creating' as const, error: null } }));
        try {
          // A retry publishes the draft a failed attempt left behind, never a second one.
          const prior = progress[item.key]?.draftId ?? null;
          let draftId = prior;
          let version: number;
          if (prior) {
            const d = await getDraft(prior);
            version = d.draft.version;
          } else {
            const input = buildExpressDraftInput({
              accountId,
              campaign: detail.data,
              adSet: set,
              template,
              name: item.name,
              status,
              feed: { url: feed.url, path: feed.path },
              story: story ? { url: story.url!, path: story.path! } : null,
            });
            const created = await createDraft(input);
            draftId = created.draft.id;
            version = created.draft.version;
            setProgress((p) => ({ ...p, [item.key]: { status: 'creating', draftId } }));
          }
          const res = await publishDraft(draftId!, version);
          setProgress((p) => ({ ...p, [item.key]: { status: 'created', adId: res.publishedMetaAdId, draftId } }));
        } catch (e) {
          setProgress((p) => ({ ...p, [item.key]: { ...p[item.key], status: 'failed' as const, error: errorText(e) } }));
        }
      }
    } finally {
      setRunning(false);
    }
  }

  const start = () => {
    setProgress(initialProgress(items));
    void run(items);
  };
  const retryFailed = () => void run(itemsToRun(items, progress));

  if (!accountId || !campaignId) {
    return (
      <div>
        <PageHeader title="Add images (express)" backHref="/advertising/meta/add-to-live" />
        <p className="text-sm text-slate-600">
          Start from the campaign list: <Link className="underline" to="/advertising/meta/add-to-live">pick a campaign</Link> and choose "Add images (express)".
        </p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Add images (express)"
        description="Drop new images into a running campaign with the same copy. Existing ads and ad sets are never changed."
        backHref="/advertising/meta/add-to-live"
      />
      {detail.isLoading && <CenteredSpinner />}
      {detail.error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {detail.error instanceof ApiError ? detail.error.message : 'Could not load that campaign.'}
        </p>
      )}
      {detail.data && (
        <div className="max-w-5xl space-y-8">
          <h2 className="text-base font-semibold text-slate-900">{detail.data.name}</h2>

          <section aria-label="Ad sets" className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">1. Ad sets</h3>
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {detail.data.adSets.map((s) => (
                <li key={s.id} className="flex items-center gap-3 px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label={`Ad set ${s.name || s.id}`}
                    checked={selectedIds.includes(s.id)}
                    disabled={started}
                    onChange={(e) => setAdSetIds(e.target.checked ? [...selectedIds, s.id] : selectedIds.filter((x) => x !== s.id))}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-900">{s.name || s.id}</span>
                  <Badge variant="neutral">{destinationLabel(s.destination)}</Badge>
                  <Badge variant={String(s.metaStatus).toUpperCase() === 'ACTIVE' ? 'success' : 'neutral'}>{s.metaStatus ?? 'Unknown'}</Badge>
                </li>
              ))}
            </ul>
          </section>

          <section aria-label="Template ad" className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">2. Template ad (its copy is reused unchanged)</h3>
            {templateAds.length === 0 ? (
              <p className="text-sm text-slate-500">Choose an ad set with ads first.</p>
            ) : (
              <select
                aria-label="Template ad"
                value={effectiveTemplateId ?? ''}
                disabled={started}
                onChange={(e) => setTemplateId(e.target.value)}
                className="max-w-md rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              >
                {templateAds.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name || a.id} ({a.metaStatus ?? 'Unknown'})
                  </option>
                ))}
              </select>
            )}
            {templateQ.isLoading && <p className="text-xs text-slate-500">Reading the ad…</p>}
            {template && (
              <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                <dt className="text-xs text-slate-500">Headline</dt>
                <dd>{template.headlines?.[0] ?? '—'}</dd>
                <dt className="text-xs text-slate-500">Primary text</dt>
                <dd className="whitespace-pre-wrap">{firstLine(template.primaryText, 400) || '—'}</dd>
                <dt className="text-xs text-slate-500">Description</dt>
                <dd>{template.descriptions?.[0] ?? '—'}</dd>
                <dt className="text-xs text-slate-500">Call to action</dt>
                <dd>{template.callToAction ?? '—'}</dd>
                <dt className="text-xs text-slate-500">{chosenSets[0]?.destination === 'instant_form' ? 'Lead form' : 'Link'}</dt>
                <dd className="break-all">{chosenSets[0]?.destination === 'instant_form' ? (template.leadGenFormId ?? '—') : (template.linkUrl ?? '—')}</dd>
                <dt className="text-xs text-slate-500">URL tags</dt>
                <dd className="break-all">{template.urlTags ?? '—'}</dd>
              </dl>
            )}
            {droppedTextCount(template) > 0 && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="express-extra-texts">
                This ad has {droppedTextCount(template)} more text option{droppedTextCount(template) === 1 ? '' : 's'}. Only the first headline, primary text and description are reused.
              </p>
            )}
            {tIssues.length > 0 && template && (
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-red-700">
                {tIssues.map((t) => (
                  <li key={t.field}>{t.message} Pick another template ad.</li>
                ))}
              </ul>
            )}
          </section>

          <section aria-label="Images" className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">3. Images</h3>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              aria-label="Add images"
              disabled={started}
              onChange={(e) => void addFiles(e.target.files)}
            />
            <p className="text-xs text-slate-500">
              Name Story versions like <code>kawana-hero_feed.jpg</code> + <code>kawana-hero_story.jpg</code> to pair them automatically (or use 9:16 vs 4:5 / 1:1 shapes). A Feed image with no Story version is allowed; Meta crops it for Stories.
            </p>
            {pairing.pairs.length > 0 && (
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200" aria-label="Creative pairs">
                {pairing.pairs.map((p) => {
                  const feed = fileById.get(p.feedId)!;
                  const story = p.storyId ? fileById.get(p.storyId) : null;
                  return (
                    <li key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                      <img src={feed.previewUrl} alt="" className="h-14 w-14 rounded object-cover" />
                      {story ? <img src={story.previewUrl} alt="" className="h-14 w-8 rounded object-cover" /> : <span className="w-8" />}
                      <span className="w-40 text-xs text-slate-500">
                        {feed.state === 'uploading' ? 'Uploading…' : feed.state === 'failed' ? (feed.error ?? 'Failed') : 'Feed ready'}
                        {pairHasNoStory(p) && <Badge variant="warning" className="ml-1">no story version</Badge>}
                      </span>
                      <select
                        aria-label={`Story image for ${p.stem}`}
                        disabled={started}
                        value={p.storyId ?? ''}
                        onChange={(e) =>
                          setPairing((cur) =>
                            e.target.value === ''
                              ? cur.pairs.find((x) => x.id === p.id)?.storyId
                                ? assignStory(cur, cur.pairs.find((x) => x.id === p.id)!.storyId!, null)
                                : cur
                              : assignStory(cur, e.target.value, p.id),
                          )
                        }
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                      >
                        <option value="">No story image</option>
                        {[...(story ? [story.id] : []), ...pairing.unassignedStoryIds].map((id) => (
                          <option key={id} value={id}>
                            {fileById.get(id)?.name ?? id}
                          </option>
                        ))}
                      </select>
                      <input
                        aria-label={`Ad name for ${p.stem}`}
                        value={names[p.id] ?? defaultAdName(templateTreeAd?.name, p.stem)}
                        disabled={started}
                        onChange={(e) => setNames((n) => ({ ...n, [p.id]: e.target.value }))}
                        className="min-w-48 flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm"
                      />
                      <Button variant="outline" size="sm" disabled={started} onClick={() => removeFile(p.feedId)}>
                        Remove
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
            {pairing.unassignedStoryIds.length > 0 && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {pairing.unassignedStoryIds.length} Story image{pairing.unassignedStoryIds.length === 1 ? '' : 's'} not matched to a Feed image:{' '}
                {pairing.unassignedStoryIds.map((id) => fileById.get(id)?.name).join(', ')}. Choose them from a pair's Story list, or remove them.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-1">
                Prefix
                <input aria-label="Name prefix" value={prefix} disabled={started} onChange={(e) => setPrefix(e.target.value)} className="w-32 rounded-md border border-slate-300 px-2 py-1" />
              </label>
              <label className="flex items-center gap-1">
                Suffix
                <input aria-label="Name suffix" value={suffix} disabled={started} onChange={(e) => setSuffix(e.target.value)} className="w-32 rounded-md border border-slate-300 px-2 py-1" />
              </label>
            </div>
          </section>

          <section aria-label="Publish" className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">4. Publish</h3>
            <div role="radiogroup" aria-label="Status" className="flex items-center gap-4 text-sm">
              {(['PAUSED', 'ACTIVE'] as const).map((s) => (
                <label key={s} className="flex items-center gap-1">
                  <input type="radio" name="express-status" checked={status === s} disabled={started} onChange={() => setStatus(s)} />
                  {s === 'PAUSED' ? 'Paused (default)' : 'Active'}
                </label>
              ))}
            </div>
            {status === 'ACTIVE' && (
              <p className="text-xs text-amber-700">Active starts spending inside each ad set's existing budget straight away.</p>
            )}
            <p className="text-sm text-slate-700" data-testid="express-count">
              {items.length === 0 ? 'No ads yet.' : `${items.length} new ad${items.length === 1 ? '' : 's'}: ${chosenSets.length} ad set${chosenSets.length === 1 ? '' : 's'} × ${pairing.pairs.length} image${pairing.pairs.length === 1 ? '' : 's'}.`}
            </p>
            {!started && (
              <Button disabled={issue !== null || running} onClick={start}>
                Create {items.length || ''} ad{items.length === 1 ? '' : 's'}
              </Button>
            )}
            {!started && issue && <p className="text-xs text-slate-500">{issue}</p>}
          </section>

          {started && (
            <section aria-label="Progress" className="space-y-2">
              <p className="text-sm text-slate-700" data-testid="express-summary">
                {counts.created} created · {counts.failed} failed · {counts.queued + counts.creating} waiting
              </p>
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {items.map((i) => {
                  const p = progress[i.key];
                  const set = chosenSets.find((s) => s.id === i.adSetId);
                  return (
                    <li key={i.key} className="flex flex-wrap items-center gap-3 px-3 py-1.5 text-sm">
                      <span className="min-w-0 flex-1 truncate">
                        {i.name} <span className="text-xs text-slate-500">in {set?.name ?? i.adSetId}</span>
                      </span>
                      <Badge variant={p?.status === 'created' ? 'success' : p?.status === 'failed' ? 'danger' : 'neutral'}>{p?.status ?? 'queued'}</Badge>
                      {p?.adId && <span className="text-xs text-slate-500">Ad {p.adId}</span>}
                      {p?.status === 'failed' && p.error && <span className="text-xs text-red-700">{p.error}</span>}
                    </li>
                  );
                })}
              </ul>
              {counts.failed > 0 && !running && (
                <Button variant="outline" onClick={retryFailed}>
                  Retry the {counts.failed} failed
                </Button>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
